import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { formatDate } from '../../../../utils/helper';
import { ordersImageBodyTemplate, ordersNameBodyTemplateLong } from './templates';
import React, { useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getOrderItemStatusInfoBase } from '../models/OrderItem';
import OrderViewStore from '../stores/OrderViewStore';

const BodyCounterReadOnlyTemplate = observer(({ product }: { product: any }) => {
    const currentCount = product?.units_after || 0; // Alte Zahl (Standardwert 0, falls nicht vorhanden)
    const oldCount = product?.units || 0; // Alte Zahl (Standardwert 0, falls nicht vorhanden)

    return (
        <div className="flex gap-1 align-items-center">
            {/* Aktuelle und alte Zahl */}
            <div className="flex flex-column align-items-center">
                {currentCount === oldCount ? (
                    <span className="flex align-items-center justify-content-center w-2rem h-2rem bg-primary font-bold border-round text-sm">
                        {currentCount}
                    </span>
                ) : (
                    <>
                        <span className="flex align-items-center justify-content-center w-2rem h-2rem bg-primary font-bold border-round text-sm">
                            {currentCount}
                        </span>
                        <span
                            className="text-xs text-secondary"
                            style={{ fontStyle: 'italic', color: '#ff6b6b' }}
                        >
                            {oldCount}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
});

const OrderView = observer(
    ({ sdk, orderViewStore }: { sdk: ApplicationSdk; orderViewStore: OrderViewStore }) => {
        if (orderViewStore === null) return null;

        const refInfo = useRef<OverlayPanel>(null);

        const useFreeProducts = orderViewStore.freeProducts || [];
        const useChargeableProducts = orderViewStore.chargeableProducts || [];

        const orderStateInfo = getOrderItemStatusInfoBase(orderViewStore.orderDelievered != null);

        const columnWidth_Image: string = '15%';
        const columnWidth_Label: string = '60%';
        const columnWidth_Value: string = '25%';

        //  return  <></>;
        return orderViewStore.isLoading ? (
            <div
                className="flex justify-content-center align-items-center"
                style={{ height: '100%' }}
            >
                <ProgressSpinner />
            </div>
        ) : (
            <div className="card">
                <div
                    className="flex align-items-center w-full pt-0 mt-0"
                    style={{ position: 'relative' }}
                >
                    {/* Platzhalter links */}
                    <div className="w-2rem" />

                    {/* Mitte: Name */}
                    <div className="flex-grow-1 text-center font-bold text-xl">
                        {orderViewStore.objectName}
                    </div>

                    {/* Rechts: Button */}
                    <div className="w-2rem flex justify-content-end">
                        <Button
                            icon="pi pi-question-circle"
                            className="p-button-text p-button-rounded"
                            onClick={e => refInfo.current?.toggle(e)}
                        />
                    </div>
                </div>

                {/* Fragezeichen-Icon rechts */}

                {/* Info-Overlay */}
                <OverlayPanel
                    ref={refInfo}
                    dismissable
                >
                    <div className="p-3 max-w-xs">
                        <h4 className="m-0 mb-2 font-semibold">Info</h4>

                        <Button
                            icon="pi pi-times"
                            className="p-button-rounded p-button-text absolute right-0 top-0"
                            onClick={() => refInfo.current?.hide()}
                        />

                        <p className="m-0 text-sm text-gray-600">
                            <span
                                className="font-bold"
                                style={{ color: '#ff6b6b' }}
                            >
                                Rote Zahl
                            </span>{' '}
                            = Ihre ursprüngliche Bestellung
                        </p>
                        <p className="m-0 text-sm text-gray-600">
                            <span className="font-bold">Schwarze Zahl</span> = Menge, die geliefert
                            wird
                        </p>
                        <p className="m-0 text-sm text-gray-600">
                            So sehen Sie sofort, wenn der Innendienst Ihre Bestellung geändert hat.
                        </p>
                    </div>
                </OverlayPanel>

                <div
                    className="pt-5"
                    style={{ textAlign: 'left' }}
                >
                    <div>Bestelldatum: {formatDate(orderViewStore.orderCreated, true)}</div>
                    <div
                        className="font-bold"
                        style={{ color: orderStateInfo.chipColor }}
                    >
                        Status: {orderStateInfo.label}
                    </div>
                </div>

                <div
                    className="p-2 mt-5 border-1 border-round-sm "
                    style={{ borderColor: 'rgba(90,88,88,0.37)' }}
                >
                    <div className="col p-0">
                        <div className="text-center p-2  bg-primary font-bold">
                            <h4 className="table-title m-0 p-0">Inklusive</h4>
                            <DataTable
                                value={[...useFreeProducts].sort((a: any, b: any) =>
                                    a.name.localeCompare(b.name)
                                )}
                                size={'small'}
                                showHeaders={false}
                                emptyMessage={'-'}
                            >
                                <Column
                                    header="Image"
                                    style={{ textAlign: 'center', width: columnWidth_Image }}
                                    body={ordersImageBodyTemplate}
                                ></Column>
                                <Column
                                    field="name"
                                    header="Name"
                                    style={{ width: columnWidth_Label }}
                                    body={ordersNameBodyTemplateLong}
                                ></Column>
                                <Column
                                    style={{ width: columnWidth_Value }}
                                    body={(p: any) => <BodyCounterReadOnlyTemplate product={p} />}
                                ></Column>
                            </DataTable>
                        </div>
                    </div>
                    <div className="col p-0">
                        <div className="text-center p-2  bg-primary font-bold">
                            <h4 className="table-title m-0 p-0">Kostenpflichtig</h4>
                            <DataTable
                                value={[...useChargeableProducts].sort((a: any, b: any) =>
                                    a.name.localeCompare(b.name)
                                )}
                                size={'small'}
                                showHeaders={false}
                                emptyMessage={'-'}
                            >
                                <Column
                                    header="Image"
                                    style={{ textAlign: 'center', width: columnWidth_Image }}
                                    body={ordersImageBodyTemplate}
                                ></Column>
                                <Column
                                    field="name"
                                    header="Name"
                                    style={{ width: columnWidth_Label }}
                                    body={ordersNameBodyTemplateLong}
                                ></Column>
                                <Column
                                    style={{ width: columnWidth_Value }}
                                    body={(p: any) => <BodyCounterReadOnlyTemplate product={p} />}
                                ></Column>
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

export default OrderView;
